import { useEffect } from 'react'
import styled from 'styled-components'
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { enGB, nb } from 'date-fns/locale'

const ImprovedDatePicker = ({
  id,
  selected,
  language = 'nb',
  onChange,
  type = 'month',
  minDate,
  maxDate = new Date(),
  disabled = false,
}: {
  id?: string
  selected: Date
  language?: 'nb' | 'enGb'
  onChange: (date: Date | false) => void
  type?: 'date' | 'month'
  minDate?: Date
  maxDate?: Date
  disabled?: boolean
}) => {
  useEffect(() => {
    const locale = language === 'nb' ? nb : enGB
    registerLocale(language, locale)
    setDefaultLocale(language)
  }, [language])

  const handleRawChange = (event) => {
    const validDate = getValidDate(event?.target?.value)
    if (validDate) {
      onChange(validDate)
    }
  }

  const handleChange = (date) => {
    const validDate = getValidDate(date)
    if (validDate) {
      onChange(validDate)
    }
  }

  const parseInputDate = (date) => {
    if (!date) return false

    if (date instanceof Date) return date

    const value = String(date).trim()
    if (!value) return false

    if (type === 'month') {
      const [month, year] = value.split('/')
      const parsedMonth = Number(month)
      const parsedYear = Number(year)

      if (!parsedMonth || !parsedYear) return false

      const parsedDate = new Date(parsedYear, parsedMonth - 1, 1)
      return Number.isNaN(parsedDate.getTime()) ? false : parsedDate
    }

    const [day, month, year] = value.split('.')
    const parsedDay = Number(day)
    const parsedMonth = Number(month)
    const parsedYear = Number(year)

    if (!parsedDay || !parsedMonth || !parsedYear) return false

    const parsedDate = new Date(parsedYear, parsedMonth - 1, parsedDay)
    return Number.isNaN(parsedDate.getTime()) ? false : parsedDate
  }

  const getValidDate = (date) => {
    const inputDate = parseInputDate(date)

    if (!inputDate) return false
    const now = maxDate || new Date()
    const isFutureMonth =
      inputDate.getFullYear() > now.getFullYear() ||
      (inputDate.getFullYear() === now.getFullYear() && inputDate.getMonth() > now.getMonth())
    const isFutureDate = inputDate > now

    if (type === 'month' ? isFutureMonth : isFutureDate) return false
    if (minDate && inputDate < minDate) return false

    return inputDate
  }

  const handleBlur = (event) => {
    if (!getValidDate(event?.target?.value)) {
      onChange(selected)
    }
  }

  const preventTextInput = (event) => {
    if (['Tab', 'Escape', 'Enter'].includes(event.key)) return
    event.preventDefault()
  }

  return (
    <DatePickerContainer>
      <DatePicker
        id={id}
        selected={selected}
        onChangeRaw={handleRawChange}
        onChange={handleChange}
        dateFormat={type === 'month' ? 'MM/yyyy' : 'dd.MM.yyyy'}
        minDate={minDate}
        maxDate={maxDate}
        onBlur={handleBlur}
        onKeyDown={preventTextInput}
        onPaste={preventTextInput}
        showMonthYearPicker={type === 'month'}
        disabled={disabled}
      />
    </DatePickerContainer>
  )
}

export default ImprovedDatePicker

const DatePickerContainer = styled.div`
  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    width: 100%;
  }

  .react-datepicker-wrapper .react-datepicker__input-container input {
    border: 1px solid var(--color-line);
    border-radius: 8px;
    padding: var(--spacing-small) var(--spacing-small);
    font-size: 12px;
    width: 100%;
    cursor: pointer;
    caret-color: transparent;
  }

  .react-datepicker__tab-loop {
    .react-datepicker-popper .react-datepicker__triangle {
      ::after,
      ::before {
        border-bottom-color: var(--color-primary);
      }
    }

    .react-datepicker-popper .react-datepicker__navigation ::before {
      border-color: var(--color-white);
    }

    .react-datepicker__month-container {
      .react-datepicker__header {
        background-color: var(--color-primary);

        div {
          color: var(--color-white);
        }
      }
    }
    .react-datepicker__month-text {
      padding: 8px 12px; /* Adjust to your preference */
      border-radius: 4px;
      cursor: pointer;
    }

    .react-datepicker__month-text:hover {
      background-color: var(--color-primary-light); /* Optional hover effect */
    }

    .react-datepicker__month-text.react-datepicker__month--selected {
      background-color: var(--color-primary);
      color: var(--color-white);
    }

    .react-datepicker__day.react-datepicker__day--023.react-datepicker__day--selected {
      background-color: var(--color-primary);
    }
  }
`
